import { HAPTIC_PATTERNS, HAPTIC_STYLES, classifyInteraction, type HapticKind } from './haptics-core.js';

type HapticOptions = { target?: Element | null; source?: string; force?: boolean };
type NativeBridge = { perform?: (payload: HapticPayload) => unknown; trigger?: (kind: string, payload?: HapticPayload) => unknown };
type HapticPayload = { version: 1; kind: HapticKind; style: string; pattern: readonly number[]; source: string };

const ENABLED_KEY = 'nosivo.haptics';
const INTERACTIVE_SELECTOR = 'button, a[href], input, select, textarea, [role="button"], [role="tab"], [role="switch"]';
const lastEmission = new Map<HapticKind, number>();
let installed = false;
let sequence = 0;

function hapticsEnabled() {
  try { return window.localStorage.getItem(ENABLED_KEY) !== 'off'; } catch { return true; }
}

export function setHapticsEnabled(enabled: boolean) {
  try { window.localStorage.setItem(ENABLED_KEY, enabled ? 'on' : 'off'); } catch { /* Storage can be unavailable in hardened WebViews. */ }
  document.documentElement.dataset.hapticsEnabled = String(enabled);
}

function minimumInterval(kind: HapticKind) {
  if (kind === 'slider') return 42;
  if (['tap', 'selection'].includes(kind)) return 55;
  if (kind === 'navigation') return 110;
  return 80;
}

function microResponse(kind: HapticKind, target?: Element | null) {
  if (!(target instanceof HTMLElement) || typeof target.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const keyframes: Partial<Keyframe>[] = ['error', 'warning', 'clear'].includes(kind)
    ? [{ transform: 'translateX(0)' }, { transform: 'translateX(-2px)' }, { transform: 'translateX(2px)' }, { transform: 'translateX(0)' }]
    : ['success', 'complete', 'create', 'water'].includes(kind)
      ? [{ transform: 'scale(1)' }, { transform: 'scale(1.018)' }, { transform: 'scale(1)' }]
      : kind === 'navigation'
        ? [{ transform: 'translateX(0)' }, { transform: 'translateX(1.5px)' }, { transform: 'translateX(0)' }]
        : [{ transform: 'scale(1)' }, { transform: 'scale(.985)' }, { transform: 'scale(1)' }];
  target.animate(keyframes, { duration: ['error', 'warning'].includes(kind) ? 150 : 115, easing: 'ease-out' });
}

function nativeFeedback(payload: HapticPayload) {
  const host = window as Window & {
    NosivoHaptics?: NativeBridge;
    flutter_inappwebview?: { callHandler?: (name: string, payload: HapticPayload) => unknown };
    webkit?: { messageHandlers?: Record<string, { postMessage?: (payload: HapticPayload) => void }> };
  };

  try {
    if (typeof host.NosivoHaptics?.perform === 'function') { host.NosivoHaptics.perform(payload); return 'native-android'; }
    if (typeof host.NosivoHaptics?.trigger === 'function') { host.NosivoHaptics.trigger(payload.kind, payload); return 'native-android'; }
    const webkitHandler = host.webkit?.messageHandlers?.nosivoHaptic;
    if (typeof webkitHandler?.postMessage === 'function') { webkitHandler.postMessage(payload); return 'native-ios'; }
    if (typeof host.flutter_inappwebview?.callHandler === 'function') { host.flutter_inappwebview.callHandler('nosivoHaptic', payload); return 'native-flutter'; }
  } catch { /* A partially installed native bridge should not break the workflow. */ }

  try {
    if (typeof navigator.vibrate === 'function' && navigator.vibrate([...payload.pattern])) return 'web-vibration';
  } catch { /* Visual feedback remains available below. */ }
  return 'visual';
}

export function emitHaptic(kind: HapticKind, options: HapticOptions = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !hapticsEnabled()) return false;
  const now = performance.now();
  const previous = lastEmission.get(kind) || 0;
  if (!options.force && now - previous < minimumInterval(kind)) return false;
  lastEmission.set(kind, now);

  const payload: HapticPayload = {
    version: 1,
    kind,
    style: HAPTIC_STYLES[kind],
    pattern: HAPTIC_PATTERNS[kind],
    source: options.source || 'interface',
  };
  const adapter = nativeFeedback(payload);
  microResponse(kind, options.target);
  sequence += 1;
  document.documentElement.dataset.hapticKind = kind;
  document.documentElement.dataset.hapticAdapter = adapter;
  document.documentElement.dataset.hapticSequence = String(sequence);
  window.dispatchEvent(new CustomEvent('nosivo:haptic', { detail: { ...payload, adapter, sequence } }));
  return true;
}

function elementDetails(element: Element) {
  const control = element as HTMLButtonElement & HTMLInputElement & HTMLAnchorElement;
  const tagName = element.tagName.toLowerCase();
  return {
    disabled: Boolean(control.disabled) || element.getAttribute('aria-disabled') === 'true',
    dataHaptic: element.getAttribute('data-haptic'),
    tagName,
    role: element.getAttribute('role'),
    inputType: tagName === 'input' ? control.type : element.getAttribute('type') || '',
    text: element.textContent || '',
    ariaLabel: element.getAttribute('aria-label'),
    title: element.getAttribute('title'),
    href: control.href || null,
  };
}

function interactiveTarget(event: Event) {
  return event.target instanceof Element ? event.target.closest(INTERACTIVE_SELECTOR) : null;
}

export function installHaptics() {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  document.documentElement.dataset.hapticsEnabled = String(hapticsEnabled());

  document.addEventListener('click', (event) => {
    const target = interactiveTarget(event);
    if (!target) return;
    const kind = classifyInteraction(elementDetails(target));
    if (kind) emitHaptic(kind, { target, source: 'click' });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target : null;
    if (form?.getAttribute('data-haptic') === 'none') return;
    emitHaptic('submit', { target: form, source: 'form-submit' });
  }, true);

  document.addEventListener('change', (event) => {
    const target = interactiveTarget(event);
    if (!target) return;
    const kind = classifyInteraction(elementDetails(target));
    if (kind === 'selection' || kind === 'slider') emitHaptic(kind, { target, source: 'change' });
  }, true);

  document.addEventListener('input', (event) => {
    const target = interactiveTarget(event);
    if (target instanceof HTMLInputElement && target.type === 'range') emitHaptic('slider', { target, source: 'slider' });
  }, true);
}
